import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { Input } from "./Input.js";
import { RangeInput } from "./RangeInput.js";
import { Container, InputContainer, IconStyled, SliderContainer } from "./styles.js";
import { DragRange } from "./DragRange.js";
import { ARROW_KEYS, DECREMENT_ARROW_KEYS } from "./Slider.constants.js";
function Slider({
  min,
  max,
  inputPosition = "none",
  value,
  onChange,
  iconLeft,
  iconRight,
  isFromCenter = false,
  isInactive = false,
  isTooltipHidden = false,
  isDisabled = false,
  step = "1",
  ...props
}) {
  const [tempInputValue, setTempInputValue] = useState("");
  const [focusedTextfield, setFocusedTextfield] = useState("none");
  const dragStartRef = useRef(0);
  const sliderRef = useRef(null);
  const isRange = Array.isArray(value);
  const valueRange = isRange ? value[1] - value[0] : 0;
  const decimalPlaces = (step == null ? void 0 : step.includes(".")) ? step.split(".")[1].length : 0;
  function getFixedPointValue(val) {
    return Number(val.toFixed(decimalPlaces));
  }
  function getMinValue(valueChange) {
    return isRange ? getFixedPointValue(Math.max(min, Math.min(max - valueRange, value[0] + valueChange))) : value;
  }
  function onDragStart(event) {
    event.preventDefault();
    dragStartRef.current = event.clientX;
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", onDragEnd);
  }
  function onDrag(event) {
    if (!isRange || !sliderRef.current) {
      return;
    }
    const stepVal = Number(step ?? 1);
    const dragRatio = (event.clientX - dragStartRef.current) / sliderRef.current.clientWidth;
    const valueChange = Math.round(dragRatio * (max - min) / stepVal) * stepVal;
    const minVal = getMinValue(valueChange);
    const maxVal = getFixedPointValue(Math.min(max, minVal + valueRange));
    onChange([minVal, maxVal]);
  }
  function onDragEnd(event) {
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", onDragEnd);
    dragStartRef.current = event.clientX;
  }
  function onKeyDown(event) {
    if (!isRange) {
      return;
    }
    const diff = Number(step ?? 1);
    if (ARROW_KEYS.includes(event.key)) {
      event.preventDefault();
      const minVal = getMinValue(DECREMENT_ARROW_KEYS.includes(event.key) ? -diff : diff);
      const maxVal = getFixedPointValue(Math.min(max, minVal + valueRange));
      return onChange([minVal, maxVal]);
    }
  }
  function getValidValue(inputValue, targetName) {
    if (isRange) {
      if (targetName === "min-input" && inputValue < min) {
        return min;
      }
      if (targetName === "max-input" && inputValue > max) {
        return max;
      }
      return inputValue;
    }
    if (inputValue > max) {
      return max;
    }
    if (inputValue < min) {
      return min;
    }
    return inputValue;
  }
  function onValueChange(event) {
    const inputValue = parseFloat(event.target.value);
    if (isRange) {
      if (event.target.name === "min-input" && inputValue < value[1]) {
        const val = getValidValue(inputValue, "min-input");
        onChange([val, value[1]]);
      }
      if (event.target.name === "max-input" && value[0] < inputValue) {
        const val = getValidValue(inputValue, "max-input");
        onChange([value[0], val]);
      }
    } else {
      const val = getValidValue(inputValue);
      onChange(val);
    }
  }
  function onInputKeyDown(event, inputValue, targetName) {
    if (event.key === "Enter") {
      event.target.blur();
    }
    if (["ArrowUp", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      let newValue = parseFloat(event.currentTarget.value) || Number(inputValue);
      switch (event.key) {
        case "ArrowUp": {
          newValue = newValue + Number(step);
          break;
        }
        case "ArrowDown": {
          newValue = newValue - Number(step);
          break;
        }
      }
      newValue = getValidValue(newValue, targetName);
      onValueChange({
        target: {
          name: targetName,
          value: String(newValue)
        }
      });
      setTempInputValue(String(newValue));
    }
  }
  const leftValue = String(isRange ? value[0] : value);
  const rightValue = String(isRange ? value[1] : value);
  return /* @__PURE__ */ jsxs(Container, {
    ...props,
    children: [(inputPosition === "left" || inputPosition === "both") && /* @__PURE__ */ jsx(InputContainer, {
      $min: min,
      $max: max,
      "data-testid": "ds-slider-input-left",
      children: /* @__PURE__ */ jsx(Input, {
        onChange: setTempInputValue,
        value: focusedTextfield === "left" ? tempInputValue : leftValue,
        type: "number",
        onFocus: () => {
          setTempInputValue(leftValue);
          setFocusedTextfield("left");
        },
        onBlur: () => {
          onValueChange({
            target: {
              name: "min-input",
              value: tempInputValue
            }
          });
          setFocusedTextfield("none");
        },
        onKeyDown: (e) => onInputKeyDown(e, leftValue, "min-input"),
        isDisabled
      })
    }), iconLeft && /* @__PURE__ */ jsx(IconStyled, {
      $isDisabled: isDisabled,
      children: iconLeft
    }), /* @__PURE__ */ jsxs(SliderContainer, {
      ref: sliderRef,
      children: [isRange && /* @__PURE__ */ jsx(DragRange, {
        max,
        min,
        value,
        onKeyDown,
        onMouseDown: onDragStart
      }), /* @__PURE__ */ jsx(RangeInput, {
        min,
        max,
        value,
        inputPosition,
        onChange: onValueChange,
        isFromCenter,
        isInactive,
        isTooltipHidden,
        isDisabled,
        step
      })]
    }), iconRight && /* @__PURE__ */ jsx(IconStyled, {
      $isDisabled: isDisabled,
      children: iconRight
    }), (inputPosition === "right" || inputPosition === "both") && /* @__PURE__ */ jsx(InputContainer, {
      $min: min,
      $max: max,
      "data-testid": "ds-slider-input-right",
      children: /* @__PURE__ */ jsx(Input, {
        onChange: setTempInputValue,
        value: focusedTextfield === "right" ? tempInputValue : rightValue,
        type: "number",
        onFocus: () => {
          setTempInputValue(rightValue);
          setFocusedTextfield("right");
        },
        onBlur: () => {
          onValueChange({
            target: {
              name: "max-input",
              value: tempInputValue
            }
          });
          setFocusedTextfield("none");
        },
        onKeyDown: (e) => onInputKeyDown(e, rightValue, "max-input"),
        isDisabled
      })
    })]
  });
}
export {
  Slider
};
//# sourceMappingURL=Slider.js.map
